import ErrorMessage from "@/v2-ui/ErrorMessage"
import Icon from "@/v2-ui/icon"
import Skeleton from "@/v2-ui/skeleton"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import { useFeatureDetailsTexts } from "@/v2-backoffice-client/feature/details/feature.details.texts"
import TranslationEditor from "@/v2-backoffice-client/translation/editor/TranslationEditor"
import { deriveCategory } from "@/v2-backoffice-client/translation/translation.utils"
import { deriveCategoryKey } from "@/v2-backoffice-client/translation/translation.board.utils"
import {
  ENGLISH_LANGUAGE_CODE,
  NORWEGIAN_BOKMAL_LANGUAGE_CODE,
  TRANSLATION_PENDING_PATH,
} from "@/v2-backoffice-client/translation/translation.constants"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

export type FeatureDetailsTextsProps = {
  featureCode: string
  // What the explainer calls the thing being edited: "layer", "basemap", …
  subject: string
  featureCodeLabel: string
  /**
   * Skip the header and the flow explainer. For a panel that embeds one editor
   * per sublayer and says where saves go once, above the rows.
   */
  compact?: boolean
}

/**
 * Authoring deployments: the real TranslationEditor against one catalog
 * entry's feature code, prefixed with a flow explainer so an operator
 * without any translation-system knowledge understands where a save goes.
 *
 * Every catalog sheet (layers, basemaps, nav layers) embeds this, so all
 * three edit text through the exact same pipeline: saves land in the
 * translation catalog, appear under Translation → Review pending, and go
 * live with the next translation publish.
 */
function FeatureDetailsTexts(props: FeatureDetailsTextsProps) {
  const { featureCode, subject, featureCodeLabel, compact = false } = props

  const { languages, feature, isLoading, error, refetch } =
    useFeatureDetailsTexts({
      featureCode,
    })

  // TranslationEditor edits a catalog row; build one for this feature from
  // the single-feature texts query (the Translation tab's catalog list
  // excludes gis codes, so the row can't come from there).
  const texts = (feature?.Texts ?? []).map((text) => ({
    languageCode: text.LanguageCode,
    name: text.Name ?? "",
    description: text.Description ?? "",
  }))
  const nameOf = (code: string) =>
    texts.find((text) => text.languageCode === code)?.name ?? ""
  const row: TranslationRow = {
    code: featureCode,
    shortCode: featureCode,
    objectType: "Feature",
    attributes: null,
    category: deriveCategory(featureCode),
    categoryKey: deriveCategoryKey(featureCode),
    linkGroupCode: feature?.LinkGroupCode ?? null,
    isLinkMaster: feature?.IsLinkMaster ?? false,
    texts,
    nbName: nameOf(NORWEGIAN_BOKMAL_LANGUAGE_CODE),
    enName: nameOf(ENGLISH_LANGUAGE_CODE),
    missingCount: languages.filter((lang) => !nameOf(lang.code)).length,
    isDeleted: feature?.IsDeleted ?? false,
  }

  if (isLoading) {
    // Skeleton in the shape of the loaded section (header, explainer bar,
    // one label + name/description row per language) so nothing jumps.
    return (
      <section className="flex flex-col gap-2" aria-busy="true">
        {!compact && (
          <>
            <header className="text-sm font-medium">
              Name &amp; description
            </header>
            <Skeleton className="h-9 w-full rounded-md" />
          </>
        )}
        <div className="flex flex-col gap-3 pt-1">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex items-center gap-3">
              <Skeleton className="h-4 w-24 shrink-0" />
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 flex-1" />
            </div>
          ))}
        </div>
      </section>
    )
  }

  // Nothing to edit because the read failed: an editor full of empty fields
  // would say "this layer has no texts", which is the one thing we don't
  // know. Say what happened and offer the retry.
  if (error && !feature) {
    return (
      <section className="flex flex-col gap-2">
        {!compact && (
          <header className="text-sm font-medium">
            Name &amp; description
          </header>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <ErrorMessage
            className="px-0"
            error={`The texts could not be loaded: ${error.message}`}
          />
          <Button
            size="xs"
            variant="outline"
            data-feature-code={`UI_Backoffice${featureCodeLabel}_Details_RetryTexts`}
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="flex flex-col gap-2">
      {!compact && (
        <>
          <header className="text-sm font-medium">Name & description</header>
          <p className="flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
            <Icon src="info" size={12} className="mt-0.5 shrink-0" />
            <span>
              This is the Features page&apos;s text editor, scoped to this{" "}
              {subject}. Saves land in the translation catalog, appear under{" "}
              <Link
                to={TRANSLATION_PENDING_PATH}
                size="xs"
                data-feature-code={`UI_Backoffice${featureCodeLabel}_Details_PendingLink`}
              >
                Features → Review pending
              </Link>{" "}
              and go live in the apps with the next translation publish.
            </span>
          </p>
        </>
      )}

      {/* key: reset the editor's draft state when the sheet switches entry */}
      <TranslationEditor
        key={row.code}
        row={row}
        languages={languages}
        canDelete={false}
        onSaved={() => void refetch()}
        onSavedTexts={() => void refetch()}
      />
    </section>
  )
}

export default FeatureDetailsTexts
